"use client";

import * as React from "react";
import { ChevronRight, Pin, Search, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DialogHeading } from "@/components/ui/dialog-heading";
import { Input } from "@/components/ui/input";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  isAdminNavigationPathActive,
  type AdminNavigationDestination,
} from "@/lib/adminNavigation";
import { usePathname } from "@/lib/router-compat";

export function NavigationFavorites({
  destinations,
  favoriteIds,
  favorites,
  favoriteLimit,
  onToggleFavorite,
}: {
  destinations: readonly AdminNavigationDestination[];
  favoriteIds: readonly string[];
  favorites: readonly AdminNavigationDestination[];
  favoriteLimit: number;
  onToggleFavorite: (id: string) => void;
}) {
  const pathname = usePathname();
  const { isMobile, setOpenMobile } = useSidebar();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [pendingRemoval, setPendingRemoval] =
    React.useState<AdminNavigationDestination | null>(null);

  const filteredDestinations = React.useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase("ja");
    if (!normalized) return destinations;

    return destinations.filter((destination) =>
      [
        destination.title,
        destination.groupLabel,
        destination.parentTitle,
        ...(destination.keywords ?? []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("ja")
        .includes(normalized)
    );
  }, [destinations, search]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setSearch("");
  };

  const handleNavigate = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <>
      <SidebarGroup
        className={`border-sidebar-border border-b group-data-[collapsible=icon]:px-1 ${
          favorites.length > 0 ? "pb-2 pt-1" : "px-2 py-1"
        }`}
      >
        {favorites.length > 0 ? (
          <div className="flex items-center gap-1 group-data-[collapsible=icon]:hidden">
            <SidebarGroupLabel className="min-w-0 flex-1">
              ピン留め
            </SidebarGroupLabel>
            <button
              type="button"
              aria-label="ピン留めを編集"
              title="ピン留めを編集"
              onClick={() => setOpen(true)}
              className="text-muted-foreground hover:bg-sidebar-accent focus-visible:ring-sidebar-ring flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2"
            >
              <Settings2 className="size-3.5" aria-hidden="true" />
            </button>
          </div>
        ) : null}
        <SidebarMenu initial={false}>
          {favorites.map((destination) => {
            const Icon = destination.icon ?? Pin;
            const active = isAdminNavigationPathActive(
              pathname,
              destination.url
            );
            return (
              <SidebarMenuItem key={destination.id}>
                <SidebarMenuButton
                  asChild
                  tooltip={destination.title}
                  isActive={active}
                  className="min-h-11 pr-12 font-normal md:min-h-8 md:pr-10"
                >
                  <Link
                    to={destination.url}
                    aria-current={active ? "page" : undefined}
                    onClick={handleNavigate}
                  >
                    <Icon aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">
                      {destination.title}
                    </span>
                  </Link>
                </SidebarMenuButton>
                <SidebarMenuAction
                  type="button"
                  aria-label={`${destination.title}のピン留めを外す`}
                  title={`${destination.title}のピン留めを外す`}
                  onClick={() => setPendingRemoval(destination)}
                  className="text-primary/75 hover:text-primary right-0 top-1/2 size-11 w-11 -translate-y-1/2 cursor-pointer peer-data-[size=default]/menu-button:top-1/2 md:size-8 md:w-8"
                >
                  <Pin data-slot="pinned-indicator" aria-hidden="true" />
                </SidebarMenuAction>
              </SidebarMenuItem>
            );
          })}
          {favorites.length === 0 ? (
            <SidebarMenuItem>
              <SidebarMenuButton
                type="button"
                tooltip="メニューをピン留め"
                onClick={() => setOpen(true)}
                className="border-sidebar-border bg-sidebar-accent/45 text-sidebar-foreground hover:bg-sidebar-accent min-h-9 cursor-pointer border text-sm font-medium md:min-h-9"
              >
                <Pin className="size-3.5" aria-hidden="true" />
                <span>メニューをピン留め</span>
                <ChevronRight className="ml-auto size-3.5" aria-hidden="true" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : null}
        </SidebarMenu>
      </SidebarGroup>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-xl">
          <DialogHeading
            className="border-b px-6 pb-4 pr-14 pt-6"
            title="メニューをピン留め"
            description={`サイドメニューの上部へ最大${favoriteLimit}件までピン留めできます。`}
          />
          <div className="border-b p-4">
            <div className="relative">
              <Search
                className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label="ピン留めする機能を検索"
                placeholder="機能名や業務用語で検索"
                className="h-11 pl-9"
              />
            </div>
          </div>
          <div className="max-h-[min(28rem,60dvh)] overflow-y-auto p-2">
            {filteredDestinations.length > 0 ? (
              <ul aria-label="ピン留め候補" className="space-y-1">
                {filteredDestinations.map((destination) => {
                  const checked = favoriteIds.includes(destination.id);
                  const disabled =
                    !checked && favoriteIds.length >= favoriteLimit;
                  const Icon = destination.icon;
                  const hierarchy = [
                    destination.groupLabel,
                    destination.parentTitle,
                  ]
                    .filter(Boolean)
                    .join(" › ");

                  return (
                    <li key={destination.id}>
                      <label className="hover:bg-accent focus-within:ring-ring flex min-h-12 cursor-pointer items-center gap-3 rounded-md px-3 py-2 transition-colors focus-within:ring-2 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => onToggleFavorite(destination.id)}
                          className="border-input text-primary focus:ring-ring size-4 rounded"
                        />
                        {Icon ? (
                          <Icon
                            className="text-muted-foreground size-4 shrink-0"
                            aria-hidden="true"
                          />
                        ) : null}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">
                            {destination.title}
                          </span>
                          <span className="text-muted-foreground block truncate text-xs">
                            {hierarchy}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-muted-foreground px-4 py-10 text-center text-sm">
                該当する機能がありません。別の言葉で検索してください。
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingRemoval !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingRemoval(null);
        }}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>ピン留めを外しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              「{pendingRemoval?.title}
              」をサイドメニューのピン留めから外します。
              機能は業務メニューや検索から引き続き利用できます。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingRemoval) return;
                onToggleFavorite(pendingRemoval.id);
                setPendingRemoval(null);
              }}
            >
              ピン留めを外す
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
